// ============================================================
// MongoDBShell.jsx
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Button,
} from "@mui/material";

/* MUI Icons */
import TerminalIcon from "@mui/icons-material/Terminal";
import JavascriptIcon from "@mui/icons-material/Javascript";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    yellow: "#F9D71C",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    terminalBg: "#010409",
};

function CommandRow({ cmd, desc }) {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(cmd);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            "&:hover": { bgcolor: "rgba(0, 237, 100, 0.05)", borderColor: BRAND.border }
        }}>
            <Box>
                <Typography variant="body2" sx={{ fontFamily: "'Fira Code', monospace", color: BRAND.green, fontWeight: 700 }}>
                    {cmd}
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted }}>{desc}</Typography>
            </Box>
            <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
        </Box>
    );
}

export default function MongoDBShell() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="CLI Mastery · Module 04"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        The <span style={{ color: BRAND.green }}>Mongo Shell</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Welcome to <code>mongosh</code>. The modern MongoDB Shell is a powerful
                        <strong> JavaScript-based</strong> environment. It’s not just for queries;
                        it’s a full programming REPL for your data.
                    </Typography>
                </Box>

                {/* 2. NAVIGATION COMMANDS */}
                <Box sx={{ mb: 8 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <TerminalIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Basic Navigation</Typography>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <CommandRow cmd="show dbs" desc="List all available databases on the server." />
                            <CommandRow cmd="use shopDB" desc="Switch to a specific database (creates it if it doesn't exist)." />
                            <CommandRow cmd="db" desc="Prints the current database you are working in." />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommandRow cmd="show collections" desc="List all collections (tables) in the current database." />
                            <CommandRow cmd="cls" desc="Clear the shell screen for a fresh start." />
                            <CommandRow cmd="exit" desc="Safely close the mongosh connection." />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. JAVASCRIPT INTEGRATION */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <JavascriptIcon sx={{ color: BRAND.green, fontSize: 40 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>It's Just JavaScript</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Because the shell is built on Node.js, you can use variables, functions, and
                            even standard JS logic. This is incredibly useful for generating test data
                            or performing batch updates.
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: "rgba(249,215,28,0.05)", border: `1px solid ${BRAND.yellow}`, borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: BRAND.yellow, fontWeight: 700 }}>
                                <HelpOutlineIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                                DID YOU KNOW?
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.white, mt: 0.5 }}>
                                You can use <code>JSON.stringify()</code> inside the shell to format your output!
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Box sx={{ bgcolor: BRAND.terminalBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, p: 3 }}>
                            <Typography variant="caption" sx={{ color: BRAND.muted, mb: 2, display: "block" }}>
                                {"// Generating 5 test users"}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Fira Code', monospace", color: BRAND.green }}>
                                {`for(let i=0; i<5; i++) {`} <br/>
                                &nbsp;&nbsp; {`db.users.insertOne({ name: "User" + i });`} <br/>
                                {`}`}
                            </Typography>
                            <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.1)" }} />
                            <Typography sx={{ fontFamily: "'Fira Code', monospace", color: BRAND.muted }}>
                                {`{ acknowledged: true, ... }`}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* 4. KEYBOARD SHORTCUTS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <CodeIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Efficiency Shortcuts</Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {[
                            { key: "TAB", action: "Autocomplete collection names or methods." },
                            { key: "Up Arrow", action: "Cycle through your previous command history." },
                            { key: "Ctrl + C", action: "Abort the current operation or exit." },
                            { key: ".help", action: "Get a list of all shell-specific help commands." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Stack direction="row" spacing={2} sx={{ p: 2, bgcolor: BRAND.surface, borderRadius: 2 }}>
                                    <Chip label={item.key} size="small" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }} />
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{item.action}</Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 5. FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <PlayArrowIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Shell Ready?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Now that you can navigate the shell, let's perform your first "Create" operation.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Practice CRUD</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>View API Docs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
